/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { create } from "zustand";

export type SidebarMode = "hidden" | "cluster" | "poiList" | "detail";

export interface SidebarCluster {
  id: number;
  position: { lat: number; lng: number };
  count: number;
  pois: any[];
}

interface MapUIState {
  // Country selection
  selectedCountryCode: string | null;

  // Pin selection & hover
  selectedPin: string | null;
  hoveredPin: string | null;
  checkedPins: Set<string>;

  // Popup data
  activePopupPoi: any | null;
  hoverPopupPoi: any | null;
  isPopupHovering: boolean;

  // Expanded Media Group in popup (null = show list, number = show expanded group)
  expandedMediaGroupId: number | null;

  //  View All Media mode
  isViewAllMediaMode: boolean;
  viewAllMediaGroupId: number | null;
  originalLocation: { lat: number; lng: number; zoom: number } | null;
  allMediaGroupLocations: Record<number, { lat: number; lng: number }[]>;
  // Pin IDs (mediaGroupPoint) to highlight in View All Media mode
  viewAllMediaPins: Set<string>;

  // Sidebar
  mode: SidebarMode;
  clusters: SidebarCluster[];
  activeClusterId: number | null;
  activePoi: any | null;
  activePoiList: any[];

  // Camera
  zoom: number;
  lat: number | null;
  lng: number | null;

  // Actions
  setCountry: (code: string | null) => void;
  clearCountry: () => void;

  setCamera: (lat: number, lng: number, zoom: number) => void;
  resetCamera: () => void;

  setVisibleClusters: (clusters: SidebarCluster[], zoom: number) => void;

  openClusterList: () => void;
  openPoiListForCluster: (clusterId: number) => void;
  openPoiDetail: (poi: any) => void;
  openPoiListForSinglePoi: (poi: any) => void;

  setSelectedPin: (id: string | null) => void;
  setHoveredPin: (id: string | null) => void;
  setCheckedPin: (id: string, checked: boolean) => void;
  clearHoveredPin: () => void;
  clearCheckedPin: () => void;
  clearSelectedPin: () => void;

  setActivePopupPoi: (poi: any | null) => void;
  setHoverPopupPoi: (poi: any | null) => void;

  setPopupHovering: (value: boolean) => void;

  // Expand/collapse Media Group in popup
  setExpandedMediaGroupId: (id: number | null) => void;
  collapseMediaGroup: () => void;

  // View All Media actions
  setAllMediaGroupLocations: (locations: Record<number, { lat: number; lng: number }[]>) => void;
  enterViewAllMediaMode: (mediaGroupId: number, currentLat: number, currentLng: number, currentZoom: number, pinIds: string[]) => void;
  exitViewAllMediaMode: () => void;

  hideSidebar: () => void;

  focusPoiFromCard: (poi: any) => void;
  resetClusterState: () => void;
}

export const useMapUIStore = create<MapUIState>((set, get) => ({
  selectedCountryCode: null,

  selectedPin: null,
  hoveredPin: null,
  checkedPins: new Set<string>(),

  activePopupPoi: null,
  hoverPopupPoi: null,
  isPopupHovering: false,

  expandedMediaGroupId: null,

  // View All Media mode
  isViewAllMediaMode: false,
  viewAllMediaGroupId: null,
  originalLocation: null,
  allMediaGroupLocations: {},
  viewAllMediaPins: new Set<string>(),

  mode: "hidden",
  clusters: [],
  activeClusterId: null,
  activePoi: null,
  activePoiList: [],

  zoom: 4,
  lat: null,
  lng: null,

  setCountry: (code) =>
    set({
      selectedCountryCode: code,
      mode: code ? "cluster" : "hidden",
    }),

  clearCountry: () =>
    set({
      selectedCountryCode: null,
      selectedPin: null,
      hoveredPin: null,
      activePopupPoi: null,
      hoverPopupPoi: null,
      isPopupHovering: false,
      mode: "hidden",
    }),

  setCamera: (lat, lng, zoom) => set({ lat, lng, zoom }),

  resetCamera: () => set({ lat: null, lng: null, zoom: 4 }),

  setVisibleClusters: (clusters, zoom) => {
    set({ clusters, zoom });
    if (get().mode === "cluster") set({ mode: "cluster" });
  },

  openClusterList: () =>
    set({
      mode: "cluster",
      activeClusterId: null,
      activePoi: null,
    }),

  openPoiListForCluster: (clusterId) =>
    set({
      mode: "poiList",
      activeClusterId: clusterId,
      activePoi: null,
    }),

  openPoiListForSinglePoi: (poi) =>
    set({
      mode: "poiList",
      activeClusterId: null,
      activePoiList: poi.mediaProducts,
    }),

  openPoiDetail: (poi) =>
    set({
      mode: "detail",
      activePoi: poi,
      selectedPin: poi.mediaGroupPoint ?? null,
      activePopupPoi: poi,
    }),

  setSelectedPin: (id) => set({ selectedPin: id }),
  setHoveredPin: (id) => set({ hoveredPin: id }),
  setCheckedPin: (id, checked) =>
    set((state) => {
      const newSet = new Set(state.checkedPins);
      if (checked) newSet.add(id);
      else newSet.delete(id);
      return { checkedPins: newSet };
    }),
  clearHoveredPin: () => {
    if (!get().isPopupHovering) set({ hoveredPin: null });
  },
  clearCheckedPin: () => set({ checkedPins: new Set() }),

  clearSelectedPin: () =>
    set({
      selectedPin: null,
      hoveredPin: null,
    }),

  setActivePopupPoi: (poi) => set({ activePopupPoi: poi, expandedMediaGroupId: null }),
  setHoverPopupPoi: (poi) => set({ hoverPopupPoi: poi }),

  setPopupHovering: (value) => set({ isPopupHovering: value }),

  setExpandedMediaGroupId: (id) => set({ expandedMediaGroupId: id }),
  collapseMediaGroup: () => set({ expandedMediaGroupId: null }),

  // View All Media actions
  setAllMediaGroupLocations: (locations) => set({ allMediaGroupLocations: locations }),

  enterViewAllMediaMode: (mediaGroupId, currentLat, currentLng, currentZoom, pinIds) =>
    set({
      isViewAllMediaMode: true,
      viewAllMediaGroupId: mediaGroupId,
      originalLocation: { lat: currentLat, lng: currentLng, zoom: currentZoom },
      viewAllMediaPins: new Set(pinIds),
    }),

  exitViewAllMediaMode: () =>
    set((state) => {
      const original = state.originalLocation;
      return {
        isViewAllMediaMode: false,
        viewAllMediaGroupId: null,
        originalLocation: null,
        viewAllMediaPins: new Set<string>(),
        // Return to original location
        lat: original?.lat ?? null,
        lng: original?.lng ?? null,
        zoom: original?.zoom ?? state.zoom,
      };
    }),

  hideSidebar: () =>
    set({
      mode: "hidden",
      activeClusterId: null,
      activePoi: null,
      selectedPin: null,
      hoveredPin: null,
      hoverPopupPoi: null,
      activePopupPoi: null,
      isPopupHovering: false,
      expandedMediaGroupId: null,
      isViewAllMediaMode: false,
      viewAllMediaGroupId: null,
      originalLocation: null,
      viewAllMediaPins: new Set<string>(),
    }),

  focusPoiFromCard: (poi: any) => {
    const pinId =
      poi.mediaGroupPoint ?? (poi.id != null ? poi.id.toString() : null);

    set({
      selectedPin: pinId,
      lat: poi.lat ?? null,
      lng: poi.lng ?? null,
      zoom: poi.zoom ?? 17,
      hoveredPin: null,
    });
  },

  resetClusterState: () =>
    set({
      clusters: [],
      activeClusterId: null,
      activePoiList: [],
      activePoi: null,
    }),
}));
